import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AuditInfoCard } from '@/app/(client)/audits/-components/AuditInfoCard';
import { AuditVersionHistory } from '@/app/(client)/audits/-components/AuditVersionHistory';
import { AuditStatCards } from '@/app/(client)/audits/-components/digitized/AuditStatCards';
import { BenchmarkScoreSummary } from '@/app/(client)/audits/-components/digitized/BenchmarkScoreSummary';
import { DigitizedPlansCard } from '@/app/(client)/audits/-components/digitized/DigitizedPlansCard';
import { GeneralNotes } from '@/app/(client)/audits/-components/digitized/GeneralNotes';
import { HighestRiskSections } from '@/app/(client)/audits/-components/digitized/HighestRiskSections';
import { OverallRating } from '@/app/(client)/audits/-components/digitized/OverallRating';
import { useAuditBenchmark } from '@/app/(client)/audits/-hooks/useAuditBenchmark';
import { useAuditCapaPlans } from '@/app/(client)/audits/-hooks/useAuditCapaPlans';
import { useAuditDigitization } from '@/app/(client)/audits/-hooks/useAuditDigitization';
import { useAuditOverviewPage } from '@/app/(client)/audits/-hooks/useAuditOverviewPage';
import {
  computeFindingStatCounts,
  isDigitizationSupportedAuditType,
} from '@/app/(client)/audits/-utils/digitized';
import { AuditPermissionsDialog } from '@/components/shared/AuditPermissionsDialog';
import { CreatePlanDialog } from '@/components/shared/CreatePlanDialog';
import { useCreatePlanDialog } from '@/hooks/useCreatePlanDialog';
import { usePermissions } from '@/hooks/usePermissions';

export const Route = createFileRoute(
  '/(client)/audits/(pages)/(detail)/$auditId/overview/',
)({
  component: AuditOverviewPage,
});

function AuditOverviewPage() {
  const { auditId } = Route.useParams();
  const {
    audit,
    isOwner,
    isUpdating,
    isRequestingAccess,
    permissionsOpen,
    setPermissionsOpen,
    historyPage,
    historySorting,
    historyFilters,
    historyRows,
    historyTotal,
    isHistoryLoading,
    historyPageSize,
    setHistoryPage,
    setHistorySorting,
    setHistoryFilters,
    handleUpdate,
    handleRequestAccess,
    handleViewFile,
    t,
  } = useAuditOverviewPage(auditId);

  const perms = usePermissions();
  const { data: digitized } = useAuditDigitization(auditId);
  const { data: plansData } = useAuditCapaPlans(auditId);
  const { result: benchmark } = useAuditBenchmark(auditId);

  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const createPlanController = useCreatePlanDialog(setCreatePlanOpen, {
    id: auditId,
    companyId: audit?.companyId ?? '',
    ...(audit?.name ? { name: audit.name } : {}),
    ...(audit?.companyName ? { companyName: audit.companyName } : {}),
  });

  if (!audit) return null;

  const isDigitized =
    isDigitizationSupportedAuditType(audit.auditTypeId) && !!digitized;
  const plans = plansData?.data ?? [];
  const statCounts = computeFindingStatCounts(digitized?.findings ?? []);

  const infoCard = (
    <AuditInfoCard
      audit={audit}
      isPending={isUpdating}
      onUpdate={handleUpdate}
      onViewCap={() => handleViewFile(audit.correctiveActionPlanFileUrl)}
      onViewReport={() => handleViewFile(audit.auditReportFileUrl)}
      readOnly={!perms.audit.canEdit}
      {...(isOwner
        ? { onViewPermissions: () => setPermissionsOpen(true) }
        : {
            onRequestAccess: handleRequestAccess,
            isRequestingAccess,
          })}
      t={t}
    />
  );

  const versionHistory = (
    <AuditVersionHistory
      rows={historyRows}
      total={historyTotal}
      page={historyPage}
      pageSize={historyPageSize}
      isLoading={isHistoryLoading}
      sorting={historySorting}
      filters={historyFilters}
      auditName={audit.name ?? ''}
      companyName={audit.companyName}
      onPageChange={setHistoryPage}
      onSortingChange={setHistorySorting}
      onFiltersChange={setHistoryFilters}
      onViewFile={url => handleViewFile(url)}
      t={t}
    />
  );

  return (
    <div className='flex flex-col gap-4 p-4 @md:p-6'>
      <AuditPermissionsDialog
        open={permissionsOpen}
        auditId={permissionsOpen ? auditId : null}
        auditName={audit.name}
        onClose={() => setPermissionsOpen(false)}
      />

      {isDigitized ? (
        <>
          {/* Rail shape mirroring the SLCP verifier detail (2/5/3 of ten):
              the overall rating and findings KPIs anchor the left rail, the
              audit details and content fill the center five columns, and the
              plans get the three-column right rail — wide enough for a plan
              card to keep its meta on one line. */}
          <div className='grid grid-cols-1 @5xl:grid-cols-10 @5xl:items-start gap-4 @5xl:gap-3'>
            <aside className='@5xl:col-start-1 @5xl:col-span-2 @5xl:sticky @5xl:top-16 flex flex-col gap-4'>
              <OverallRating
                rating={digitized.overall_rating}
                auditedBy={audit.auditedBy}
                t={t}
              />
              {/* The template's reading of the audit, directly under the
                  audit's own rating — the same pairing the score tab leads
                  with. Read-only here: applying, re-applying and removing
                  belong to the score tab, so the card renders without its
                  button row. */}
              {benchmark ? (
                <BenchmarkScoreSummary result={benchmark} t={t} />
              ) : null}
              <AuditStatCards counts={statCounts} t={t} />
            </aside>
            <div className='@5xl:col-start-3 @5xl:col-span-5 min-w-0 flex flex-col gap-4'>
              {infoCard}
              <GeneralNotes
                description={digitized.description}
                summary={digitized.summary}
                t={t}
              />
              <HighestRiskSections findings={digitized.findings} t={t} />
              {versionHistory}
            </div>
            <aside className='@5xl:col-start-8 @5xl:col-span-3 @5xl:sticky @5xl:top-16'>
              <DigitizedPlansCard
                plans={plans}
                {...(perms.capa.canCreate
                  ? { onCreatePlan: () => setCreatePlanOpen(true) }
                  : {})}
                t={t}
              />
            </aside>
          </div>
        </>
      ) : (
        // Non-digitized audits get the same rail layout: audit details and
        // version history in the main column, linked plans in the same
        // three-of-ten right rail as the digitized shape above.
        <div className='grid grid-cols-1 @5xl:grid-cols-10 @5xl:items-start gap-4 @5xl:gap-3'>
          <div className='@5xl:col-start-1 @5xl:col-span-7 min-w-0 flex flex-col gap-4'>
            {infoCard}
            {versionHistory}
          </div>
          <aside className='@5xl:col-start-8 @5xl:col-span-3 @5xl:sticky @5xl:top-16'>
            <DigitizedPlansCard plans={plans} t={t} />
          </aside>
        </div>
      )}

      <CreatePlanDialog
        open={createPlanOpen}
        onOpenChange={setCreatePlanOpen}
        controller={createPlanController}
      />
    </div>
  );
}
